import { api } from "@/api";
import { TicketCategoryType } from "@/api/tickets/tickets";

import {
  setTicketCategories,
  setLocations,
  setLocks,
  setLockSystems,
  setOffices,
  setOfficeTypes,
  setRenters,
  setUsers,
  setUserRoles,
  setRequisites,
  setAgreements,
  setPayments,
  setRenterRequisites,
  setLandlords,
  setDeposits,
  setBuildingTypes,
  setCities,
  setWorkplaces,
} from "@/app/store/handbooks/reducer";
import { setChangeLogRecords } from "@/app/store/user/reducer";

type HandbookMapEntry = {
  apiCall: (args?: any) => Promise<any>;
  action: (data: any) => any;
};

export type Handbook =
  | "ticketCategories"
  | "locations"
  | "locks"
  | "lockSystems"
  | "offices"
  | "officeTypes"
  | "renters"
  | "users"
  | "userRoles"
  | "requisites"
  | "agreements"
  | "billings"
  | "renterRequisites"
  | "landlords"
  | "deposits"
  | "buildingTypes"
  | "cities"
  | "changeLogs"
  | "workplaces";

export const handbookMap: Record<Handbook, HandbookMapEntry> = {
  ticketCategories: {
    apiCall: () => {
      return new Promise((resolve, reject) => {
        const types = ["user_to_landlord", "to_platform"];
        const promises = types.map((type) =>
          api.tickets.categories.list(type as TicketCategoryType),
        );
        Promise.all(promises)
          .then((data) => resolve({ data: data.flatMap((el) => el.data) }))
          .catch(reject);
      });
    },
    action: setTicketCategories,
  },
  locations: {
    apiCall: () => api.locations.list(),
    action: setLocations,
  },
  locks: {
    apiCall: () => api.locks.list(),
    action: setLocks,
  },
  lockSystems: {
    apiCall: () => api.lockSystems.list(),
    action: setLockSystems,
  },
  offices: {
    apiCall: () => api.offices.list(),
    action: setOffices,
  },
  officeTypes: {
    apiCall: () => api.officeTypes.list(),
    action: setOfficeTypes,
  },
  renters: {
    apiCall: () => api.renters.list(),
    action: setRenters,
  },
  users: {
    apiCall: () => api.users.list(),
    action: setUsers,
  },
  userRoles: {
    apiCall: () => api.userRoles.list(),
    action: setUserRoles,
  },
  requisites: {
    apiCall: () => api.requisites.list(),
    action: setRequisites,
  },
  renterRequisites: {
    apiCall: () => api.requisites.getRenterRequisites(),
    action: setRenterRequisites,
  },
  agreements: {
    apiCall: () => api.agreements.list(),
    action: setAgreements,
  },
  billings: {
    apiCall: (args?: { period: string; year: number; location: number }) => {
      // @ts-expect-error - args is not optional
      return api.billings.list(args?.period, args?.year, args?.location);
    },
    action: setPayments,
  },
  landlords: {
    apiCall: () => api.businessProfiles.getLandlords(),
    action: setLandlords,
  },
  deposits: {
    apiCall: () => api.billings.getDeposits(),
    action: setDeposits,
  },
  buildingTypes: {
    apiCall: () => api.locations.getBuildingTypes(),
    action: setBuildingTypes,
  },
  cities: {
    apiCall: () => api.locations.listCities(),
    action: setCities,
  },
  changeLogs: {
    apiCall: () => api.changeLogs.get(),
    action: setChangeLogRecords,
  },
  workplaces: {
    apiCall: () => api.offices.workplaces.list(),
    action: setWorkplaces,
  },
};
